<template>
  <div id="recommended-experiences" class="bg-beige1 py-10 md:py-20" data-testid="recommended-experiences-section">
    <div class="md:m-auto md:w-11/12 md:max-w-[1216px]">
      <div>
        <h2 class="text-center text-xl font-normal md:text-2xl">Recommended Experiences</h2>
        <div class="pb-7 pt-2 md:pb-14">
          <div class="mx-auto w-12 border-b border-gold1 md:w-14" />
        </div>
      </div>
      <ThemeNavigationSection :destination="destinationHubLink" :themes="themeHubLinks" />
      <AlgoliaRecommendSection
        v-if="props.relatedExperiences && props.relatedExperiences.length > 0"
        recommendation-type="you_might_also_like"
        title="You Might Also Like"
        :experiences="props.relatedExperiences"
        :source-experience-slug="props.currentExperienceSlug ?? ''"
      />
      <AlgoliaRecommendSection
        v-if="popularInDestinationTitle && props.popularInDestinationExperiences && props.popularInDestinationExperiences.length > 0"
        recommendation-type="popular_in_area"
        :experiences="props.popularInDestinationExperiences"
        :source-experience-slug="props.currentExperienceSlug ?? ''"
        :title="popularInDestinationTitle"
        :title-href="props.popularInDestinationHref"
      />
      <ExperiencesSection
        v-if="props.experienceSections.length > 0"
        :experience-sections="props.experienceSections"
        :is-emphasis="false"
        :on-experience-click="props.onExperienceClick"
        :on-view-all="props.onViewAll"
      />
      <ClientOnly>
        <FeaturedSection v-if="props.themeList && props.themeList.length > 0" class="pb-12 md:pb-28" type="theme" :featured-list="props.themeList" :on-featured-click="handleFeaturedClick" />
      </ClientOnly>
      <BannerSection />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import BannerSection from '@/wabunka-src/common/components/pure/BannerSection/BannerSection.vue';
import AlgoliaRecommendSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/AlgoliaRecommendSection/AlgoliaRecommendSection.vue';
import ExperiencesSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/ExperiencesSection/ExperiencesSection.vue';
import ThemeNavigationSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/ThemeNavigationSection/ThemeNavigationSection.vue';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import type { ExperienceSectionListViewModel } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceTagListViewModel } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';
import { buildDestinationHubLink, buildThemeHubLinks } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTagHubLink';
import { FeaturedSection, type MinimumFeaturedListViewModel } from '@/wabunka-src/features/Featured';
interface Props {
  experienceSections: ExperienceSectionListViewModel;
  experienceTags?: WabunkaExperienceTagListViewModel;
  themeList?: MinimumFeaturedListViewModel;
  relatedExperiences?: WabunkaExperienceViewModel[];
  popularInDestinationExperiences?: WabunkaExperienceViewModel[];
  popularInDestinationName?: string;
  popularInDestinationHref?: string;
  currentExperienceSlug?: string;
  onExperienceClick?: (sectionId: string, experienceSlug: string) => void;
  onViewAll?: (sectionId: string) => void;
}

const route = useRoute();

const props = defineProps<Props>();

const destinationHubLink = computed(() => buildDestinationHubLink(props.experienceTags ?? []));
const themeHubLinks = computed(() => buildThemeHubLinks(props.experienceTags ?? []));

const popularInDestinationTitle = computed(() => (props.popularInDestinationName ? `Popular in ${props.popularInDestinationName}` : ''));

const handleFeaturedClick = (type: string, id: string) => {
  sendGAWabunkaSectionSlugViewEvent(type, id, route.name as string);
};
</script>
